<script setup lang="ts">
import { ElNotification, type FormInstance, type FormRules } from 'element-plus'
import { ref, reactive, nextTick } from 'vue'
import { LoadStatus } from '@/utils'
import { modifyPassword, type PasswordReq } from '@/api/common'

/** 校验6-15位，字母数字和 . _ @ # */
const EXG_PASSWORD = /^[a-zA-Z0-9._@#]{6,15}$/

/** 弹窗显示modal */
const dialogVisible = ref(false)

/** form实例 */
const formRef = ref<FormInstance>()

/** 提交接口 */
const confirmLoadStatus = ref<keyof typeof LoadStatus>(LoadStatus.none)

/** interface 修改密码表单 */
interface IModifyPasswordForm extends PasswordReq {
  password_new_repeat: string
}

/** 表单-对象 用于初始化 */
const INIT_FORM: IModifyPasswordForm = {
  oldPassword: '',
  newPassword: '',
  password_new_repeat: '',
}

/** 表单-对象 */
let form = reactive<IModifyPasswordForm>({ ...INIT_FORM })

/** 密码正则、是否一致校验 */
const validatorPassword = (_rule: any, value: any, callback: any) => {
  if (!value) callback(new Error('请输入新密码'))
  else if (!EXG_PASSWORD.test(value)) callback(new Error('由数字或26个英文字母或[@ # _ .]组成，长度为6-15位'))
  else if (
    EXG_PASSWORD.test(form.password_new_repeat) &&
    EXG_PASSWORD.test(form.newPassword) &&
    form.password_new_repeat !== form.newPassword
  )
    callback(new Error('新旧密码不一致'))
  else callback()
}

/** 失去焦点时校验一致性 */
const validatorRepeat = (field: string) => {
  formRef.value!.validateField(field)
}
/** 表单校验规则 */
const rules = reactive<FormRules>({
  oldPassword: {
    message: '请输入原密码',
    required: true,
  },
  newPassword: {
    required: true,
    validator: validatorPassword,
  },
  password_new_repeat: {
    required: true,
    validator: validatorPassword,
  },
})

const emit = defineEmits<{
  (event: 'refresh'): void
}>()

/** 提交表单 */
const onSubmit = async () => {
  try {
    confirmLoadStatus.value = LoadStatus.loading
    await formRef.value!.validate()
    const { oldPassword, newPassword } = form
    await modifyPassword({ oldPassword, newPassword })
    ElNotification({
      title: '成功',
      message: '修改成功',
      type: 'success',
    })
    confirmLoadStatus.value = LoadStatus.none
    dialogVisible.value = false
    emit('refresh')
  } catch (error) {
    confirmLoadStatus.value = LoadStatus.error
  }
}

/** 编辑 */
const onEdit = async () => {
  Object.assign(form, INIT_FORM)
  confirmLoadStatus.value = LoadStatus.none
  dialogVisible.value = true
  await nextTick()
  formRef.value!.clearValidate()
}

defineExpose({ onEdit })
</script>

<template>
  <el-dialog v-model="dialogVisible" title="修改密码" :close-on-click-modal="false" :width="600"  :append-to-body="true">
    <el-form :model="form" ref="formRef" :rules="rules" :label-width="120" class="password_form" label-suffix="：">
      <el-form-item label="原密码" prop="oldPassword">
        <el-input
          v-model="form.oldPassword"
          :maxlength="50"
          clearable
          placeholder="输入原密码"
          type="password"
          show-password
          size="large"
        />
      </el-form-item>
      <el-form-item label="新密码" prop="newPassword">
        <el-input
          v-model="form.newPassword"
          :maxlength="20"
          clearable
          placeholder="输入新密码"
          @blur="validatorRepeat('password_new_repeat')"
          type="password"
          show-password
          size="large"
        />
      </el-form-item>
      <el-form-item label="确认密码" prop="password_new_repeat">
        <el-input
          v-model="form.password_new_repeat"
          :maxlength="20"
          clearable
          placeholder="输入确认密码"
          @blur="validatorRepeat('newPassword')"
          type="password"
          show-password
          size="large"
        />
      </el-form-item>
    </el-form>

    <template #footer>
      <span class="dialog-footer">
        <el-button @click="dialogVisible = false" :disabled="confirmLoadStatus === LoadStatus.loading">返回</el-button>
        <el-button type="primary" @click="onSubmit" :loading="confirmLoadStatus === LoadStatus.loading">
          确认
        </el-button>
      </span>
    </template>
  </el-dialog>
</template>
<style lang="scss" scoped>
  .password_form {
    :deep(.el-input) {
      --el-input-border-radius: 0;
      .el-input__wrapper {
        box-shadow: none !important;
        border-bottom: 1px solid #D8D8D8;
        &.is-focus {
          border-bottom: 1px solid var(--el-color-danger);
        }
      }
    }
  }
</style>